import { useState, useMemo } from 'react'
import { useAuth } from '@/contexts/AuthContext'
import { useModerator } from '@/hooks/useModerator'
import { Navigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { format } from 'date-fns'
import UploadArticleModal from '@/components/UploadArticleModal'

interface NewsArticle {
  id: string
  title: string
  description: string | null
  author: string
  publish_date: string | null
  image_url: string | null
  article_file_url: string | null
  is_displayed: boolean
  expiry_days: number | null
  created_at: string
  updated_at: string
}

type SortField = 'title' | 'author' | 'publish_date' | 'created_at'
type SortDirection = 'asc' | 'desc'

export default function Uploads() {
  const { user } = useAuth()
  const isModerator = useModerator()
  const queryClient = useQueryClient()

  const [showModal, setShowModal] = useState(false)
  const [editingArticle, setEditingArticle] = useState<NewsArticle | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [sortField, setSortField] = useState<SortField>('created_at')
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc')

  // Redirect non-moderators
  if (!user || !isModerator) {
    return <Navigate to="/" replace />
  }

  // Fetch all news articles
  const { data: articles = [], isLoading } = useQuery({
    queryKey: ['admin-news-articles'],
    queryFn: async () => {
      try {
        const { data, error } = await supabase
          .from('news_articles')
          .select('*')
          .order('created_at', { ascending: false })

        if (error) {
          console.warn('Error fetching news articles:', error)
          return []
        }
        return (data || []) as NewsArticle[]
      } catch (err) {
        console.warn('Error in news articles query:', err)
        return []
      }
    },
    retry: false,
  })

  // Delete article mutation
  const deleteArticleMutation = useMutation({
    mutationFn: async (articleId: string) => {
      const { error } = await supabase
        .from('news_articles')
        .delete()
        .eq('id', articleId)

      if (error) {
        throw new Error(`Failed to delete article: ${error.message}`)
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['news-articles'] })
      queryClient.invalidateQueries({ queryKey: ['admin-news-articles'] })
    },
  })

  // Filter and sort articles
  const filteredAndSortedArticles = useMemo(() => {
    let filtered = articles

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase()
      filtered = filtered.filter(
        (article) =>
          article.title.toLowerCase().includes(query) ||
          article.author.toLowerCase().includes(query) ||
          (article.description?.toLowerCase().includes(query) ?? false)
      )
    }

    // Apply sorting
    filtered = [...filtered].sort((a, b) => {
      let aValue: string | number
      let bValue: string | number

      switch (sortField) {
        case 'title':
          aValue = a.title.toLowerCase()
          bValue = b.title.toLowerCase()
          break
        case 'author':
          aValue = a.author.toLowerCase()
          bValue = b.author.toLowerCase()
          break
        case 'publish_date':
          aValue = a.publish_date ? new Date(a.publish_date).getTime() : 0
          bValue = b.publish_date ? new Date(b.publish_date).getTime() : 0
          break
        case 'created_at':
        default:
          aValue = new Date(a.created_at).getTime()
          bValue = new Date(b.created_at).getTime()
          break
      }

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1
      return 0
    })

    return filtered
  }, [articles, searchQuery, sortField, sortDirection])

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortDirection('asc')
    }
  }

  const handleEdit = (article: NewsArticle) => {
    setEditingArticle(article)
    setShowModal(true)
  }

  const handleDelete = async (articleId: string) => {
    if (window.confirm('Are you sure you want to delete this article?')) {
      try {
        await deleteArticleMutation.mutateAsync(articleId)
      } catch (err) {
        console.error('Error deleting article:', err)
        alert('Failed to delete article. Please try again.')
      }
    }
  }

  const handleNewArticle = () => {
    setEditingArticle(null)
    setShowModal(true)
  }

  const handleModalClose = () => {
    setShowModal(false)
    setEditingArticle(null)
  }

  const handleModalSuccess = () => {
    // Modal will close itself and invalidate queries
  }

  const getSortIcon = (field: SortField) => {
    if (sortField !== field) return '↕️'
    return sortDirection === 'asc' ? '↑' : '↓'
  }

  return (
    <div className="max-w-7xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <h1
          className="text-3xl font-bold"
          style={{ color: 'var(--text-primary)' }}
        >
          News Articles Management
        </h1>
        <button
          onClick={handleNewArticle}
          className="px-6 py-2 font-semibold rounded"
          style={{
            backgroundColor: 'var(--bg-interactive)',
            color: 'var(--text-inverse)',
          }}
        >
          New Article
        </button>
      </div>

      {/* Search and Filters */}
      <div className="mb-6 space-y-4">
        <div>
          <input
            type="text"
            placeholder="Search articles by title, author, or description..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full px-4 py-2 rounded border-2"
            style={{
              backgroundColor: 'var(--bg-secondary)',
              borderColor: 'var(--bg-tertiary)',
              color: 'var(--text-primary)',
            }}
          />
        </div>
      </div>

      {/* Articles Table */}
      {isLoading ? (
        <p style={{ color: 'var(--text-primary)' }}>Loading articles...</p>
      ) : filteredAndSortedArticles.length === 0 ? (
        <div
          className="p-6 rounded border-2"
          style={{
            backgroundColor: 'var(--bg-secondary)',
            borderColor: 'var(--bg-tertiary)',
          }}
        >
          <p style={{ color: 'var(--text-primary)' }}>
            {searchQuery
              ? 'No articles found matching your search.'
              : 'No articles uploaded yet. Click "New Article" to get started.'}
          </p>
        </div>
      ) : (
        <div
          className="rounded border-2 overflow-hidden"
          style={{
            backgroundColor: 'var(--bg-secondary)',
            borderColor: 'var(--bg-tertiary)',
          }}
        >
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr style={{ backgroundColor: 'var(--bg-tertiary)' }}>
                  <th
                    className="px-4 py-3 text-left cursor-pointer"
                    onClick={() => handleSort('title')}
                    style={{ color: 'var(--text-primary)' }}
                  >
                    Title {getSortIcon('title')}
                  </th>
                  <th
                    className="px-4 py-3 text-left cursor-pointer"
                    onClick={() => handleSort('author')}
                    style={{ color: 'var(--text-primary)' }}
                  >
                    Author {getSortIcon('author')}
                  </th>
                  <th
                    className="px-4 py-3 text-left cursor-pointer"
                    onClick={() => handleSort('publish_date')}
                    style={{ color: 'var(--text-primary)' }}
                  >
                    Publish Date {getSortIcon('publish_date')}
                  </th>
                  <th
                    className="px-4 py-3 text-left cursor-pointer"
                    onClick={() => handleSort('created_at')}
                    style={{ color: 'var(--text-primary)' }}
                  >
                    Created {getSortIcon('created_at')}
                  </th>
                  <th className="px-4 py-3 text-left" style={{ color: 'var(--text-primary)' }}>
                    Displayed
                  </th>
                  <th className="px-4 py-3 text-left" style={{ color: 'var(--text-primary)' }}>
                    Expiry Days
                  </th>
                  <th className="px-4 py-3 text-left" style={{ color: 'var(--text-primary)' }}>
                    Files
                  </th>
                  <th className="px-4 py-3 text-right" style={{ color: 'var(--text-primary)' }}>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredAndSortedArticles.map((article, index) => (
                  <tr
                    key={article.id}
                    style={{
                      backgroundColor:
                        index % 2 === 0 ? 'var(--bg-secondary)' : 'var(--bg-primary)',
                    }}
                  >
                    <td className="px-4 py-3" style={{ color: 'var(--text-primary)' }}>
                      <div className="font-semibold">{article.title}</div>
                      {article.description && (
                        <div
                          className="text-sm mt-1 line-clamp-2"
                          style={{ color: 'var(--text-secondary)' }}
                        >
                          {article.description}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3" style={{ color: 'var(--text-primary)' }}>
                      {article.author}
                    </td>
                    <td className="px-4 py-3" style={{ color: 'var(--text-primary)' }}>
                      {article.publish_date
                        ? format(new Date(article.publish_date), 'dd/MM/yyyy')
                        : '—'}
                    </td>
                    <td className="px-4 py-3" style={{ color: 'var(--text-primary)' }}>
                      {format(new Date(article.created_at), 'dd/MM/yyyy')}
                    </td>
                    <td className="px-4 py-3" style={{ color: 'var(--text-primary)' }}>
                      <span
                        className="px-2 py-1 rounded text-xs font-semibold"
                        style={{
                          backgroundColor: article.is_displayed
                            ? 'var(--color-success)'
                            : 'var(--color-error)',
                          color: 'var(--text-inverse)',
                        }}
                      >
                        {article.is_displayed ? 'Yes' : 'No'}
                      </span>
                    </td>
                    <td className="px-4 py-3" style={{ color: 'var(--text-primary)' }}>
                      {article.expiry_days ? `${article.expiry_days} days` : '—'}
                    </td>
                    <td className="px-4 py-3" style={{ color: 'var(--text-primary)' }}>
                      <div className="flex flex-col gap-1 text-sm">
                        {article.image_url && (
                          <span
                            className="px-2 py-1 rounded text-xs"
                            style={{
                              backgroundColor: 'var(--bg-tertiary)',
                              color: 'var(--text-primary)',
                            }}
                          >
                            Image
                          </span>
                        )}
                        {article.article_file_url && (
                          <span
                            className="px-2 py-1 rounded text-xs"
                            style={{
                              backgroundColor: 'var(--bg-tertiary)',
                              color: 'var(--text-primary)',
                            }}
                          >
                            PDF
                          </span>
                        )}
                        {!article.image_url && !article.article_file_url && (
                          <span style={{ color: 'var(--text-secondary)' }}>—</span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-2 justify-end">
                        <button
                          onClick={() => handleEdit(article)}
                          className="px-3 py-1 text-sm font-semibold rounded"
                          style={{
                            backgroundColor: 'var(--bg-interactive)',
                            color: 'var(--text-inverse)',
                          }}
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDelete(article.id)}
                          disabled={deleteArticleMutation.isPending}
                          className="px-3 py-1 text-sm font-semibold rounded disabled:opacity-50"
                          style={{
                            backgroundColor: 'var(--color-error)',
                            color: 'var(--text-inverse)',
                          }}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Upload/Edit Modal */}
      {showModal && (
        <UploadArticleModal
          article={editingArticle}
          onClose={handleModalClose}
          onSuccess={handleModalSuccess}
        />
      )}
    </div>
  )
}
